import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  Typography,
  Table,
  message,
  Modal,
  Input,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';
import type { BundleDetailDTO } from '@zero/shared';

interface Props {
  bundleId: number | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const BundleDetailDrawer: React.FC<Props> = ({ bundleId, open, onClose, onChanged }) => {
  const [bundle, setBundle] = useState<BundleDetailDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = async () => {
    if (bundleId == null) return;
    setLoading(true);
    try {
      const res = await request.get<BundleDetailDTO>(`/admin/bundles/${bundleId}`);
      setBundle(res.data as unknown as BundleDetailDTO);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && bundleId != null) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bundleId]);

  const approve = async () => {
    if (bundleId == null) return;
    try {
      await request.post(`/admin/bundles/${bundleId}/review`, { action: 'approve' });
      message.success('已通过');
      onChanged();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const reject = async () => {
    if (bundleId == null) return;
    if (!reason.trim()) return message.error('请填写驳回理由');
    try {
      await request.post(`/admin/bundles/${bundleId}/review`, { action: 'reject', reason });
      message.success('已驳回');
      setRejectOpen(false);
      setReason('');
      onChanged();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const handleClose = () => {
    setBundle(null);
    setRejectOpen(false);
    setReason('');
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={720}
      destroyOnClose
      title={
        bundle ? (
          <Space>
            {bundle.title}
            <Tag>{bundle.status}</Tag>
          </Space>
        ) : (
          '套图详情'
        )
      }
      extra={
        bundle?.status === 'pending_review' && (
          <Space>
            <Button type="primary" onClick={approve}>
              通过
            </Button>
            <Button danger onClick={() => setRejectOpen(true)}>
              驳回
            </Button>
          </Space>
        )
      }
    >
      {loading || !bundle ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="创作者">
                {bundle.creatorDisplayName || `#${bundle.creatorId}`}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag>{bundle.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="价格">
                {bundle.priceRmbCents != null
                  ? `¥${(bundle.priceRmbCents / 100).toFixed(2)}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="币价">{bundle.priceCoins ?? '—'} 币</Descriptions.Item>
              <Descriptions.Item label="fal 成本">
                {bundle.totalFalCostUsd != null ? `$${bundle.totalFalCostUsd.toFixed(4)}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="创作者分成">
                {bundle.creatorShareCents != null
                  ? `¥${(bundle.creatorShareCents / 100).toFixed(2)}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="销量">{bundle.salesCount}</Descriptions.Item>
              <Descriptions.Item label="热度">{bundle.heat}</Descriptions.Item>
              <Descriptions.Item label="发布时间" span={2}>
                {bundle.publishedAt ? dayjs(bundle.publishedAt).format('YYYY-MM-DD HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {bundle.description || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="上传要求" span={2}>
                {bundle.uploadInstruction || '—'}（最少 {bundle.requiredUploadCount} 张，最多{' '}
                {bundle.maxUploadCount} 张）
              </Descriptions.Item>
              {bundle.rejectionReason && (
                <Descriptions.Item label="驳回理由" span={2}>
                  {bundle.rejectionReason}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title={`指令列表（${bundle.instructions.length}）`} size="small">
            <Table
              rowKey="id"
              pagination={false}
              size="small"
              dataSource={bundle.instructions}
              columns={[
                { title: '#', dataIndex: 'orderIndex', width: 50 },
                { title: '标题', dataIndex: 'title' },
                {
                  title: '模型',
                  dataIndex: 'modelSlug',
                  render: (v) => <Tag color="purple">{v}</Tag>,
                },
                {
                  title: 'Prompt',
                  dataIndex: 'promptText',
                  render: (v) =>
                    v ? (
                      <Typography.Text ellipsis style={{ maxWidth: 240 }}>
                        {v}
                      </Typography.Text>
                    ) : (
                      '—'
                    ),
                },
              ]}
            />
          </Card>
        </Space>
      )}

      <Modal
        open={rejectOpen}
        title="驳回套图"
        onOk={reject}
        onCancel={() => setRejectOpen(false)}
      >
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="请填写清晰的驳回理由"
        />
      </Modal>
    </Drawer>
  );
};

export default BundleDetailDrawer;
